//************************************************************************ /
// STORY CARD
// Displays a story. Is tied to a specific user (author) has a header with their info.
// Users can "like" and "submit a comment". They can also read the first comment submitted.
//************************************************************************ /

import { useState, useCallback } from "react";
import Link from "next/link";
import "../styles/story-card.scss";
import {
  Card,
  Divider,
  DecoratedImage,
  Spin,
  Form,
  Typography,
  Group,
} from "@dreambigger/design-system/src/components";
import {
  CardActions,
  CardAvatarHeader,
  CardCommentInput,
  CardComment,
} from "./card-components";
import anim from "../styles/animations.module.scss";
import {
  StoryCommunityFeedItem,
  Story,
  StoryImage,
  Comment,
  User,
} from "../../../types";
import { generateCloudinaryUrl, optimizeCloudinaryImg } from "../../../utils";
import truncate from "truncate";
import { commentApi } from "../../../api/weserve/api-helper";
import { useTryAsync } from "no-try";
import { message } from "@dreambigger/design-system";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "../../../utils/inspire-analytics-helper";
import { useGA, useSegment, useMixpanel } from "@dreambigger/shared/src/hooks";

const { Text } = Typography;

type StoryCardProps = {
  feedItem: StoryCommunityFeedItem | Story;
  userLoading: boolean;
  showComments: boolean;
  currentUser?: User;
};

const StoryCard = ({
  feedItem,
  currentUser,
  userLoading,
  showComments,
}: StoryCardProps) => {
  const {
    user,
    images,
    lastComment,
    commentCount,
    likes,
    iLiked,
    description,
    collection,
    event,
    timestamp,
    hasUploadedImages,
    id,
  } = feedItem;
  // State to keep track if the user is adding a comment.
  const [addingComment, setAddingComment] = useState(false);
  const [commentForm] = Form.useForm();
  // State to hold any new comments added by the user
  const [newComments, setNewComments] = useState<Comment[]>([]);
  const [savedLastComment, setSavedLastComment] = useState(lastComment);

  const router = useRouter();
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  const storiesUrl = `/stories/${id}`;

  // We only want to lead the user to the story if they click above the comment + card sections
  const handleStoryClick = () => {
    router.push(storiesUrl);
  };

  // Function passed to <CommentInput>
  const handleCommentInputSubmit = useCallback(
    async (values) => {
      if (!values["comment"]) return;

      setAddingComment(true);

      const comment = values["comment"];

      // Allow the user to post a comment to the story.
      const [error, commentResponse] = await useTryAsync(() =>
        commentApi.postCommentToStory(id, comment)
      );

      // Log the click of the submit button
      googleAnalytics.event({
        category: `Comment on Post`,
        action: `Comment Post`, // This seems like an error, but it was how it was logged in legacty app.
        label: "Comment Post",
        value: 1,
      });

      /**
       * Display an error if the comment was unsucessfully added
       * otherwise append the comment to the newComments array.
       */
      if (error) {
        message.error("Unable to add your comment");
        setAddingComment(false);
      } else {
        setNewComments([...newComments, commentResponse.data]);
        commentForm.resetFields();
        setAddingComment(false);
      }
    },
    [newComments]
  );

  // NOTE: A Story should always be guaranteed at least one image according to backend logic. Either user uploaded or a standard background image.
  const getCardImage = (images?: StoryImage[]) => {
    if (!images?.[0]) {
      return "";
    }

    if (images[0].meta?.src) {
      return optimizeCloudinaryImg(images[0].meta.src);
    }

    if (images[0].imageVersion && images[0].imageId) {
      return generateCloudinaryUrl(images[0].imageVersion, images[0].imageId);
    }

    return "";
  };

  /**
   * Display text next to the Story Category Line of the Card
   *  - If the story is associated to an event, display the event name.
   *  - If the story is associated to a collection, display the shareCtaCollection.
   */
  const storyCategoryDescription =
    event?.name || collection?.shareCtaCollection;

  // Truncate the story down to 140 characters if applicable.
  const storyDescription = truncate(description || "", 140);

  // Get either the first image the user uploaded or the default background image assigned to the story.
  const storyImage = getCardImage(images);

  const handleCardCommentInputClick = () => {
    googleAnalytics.event({
      category: `Comment on Post`,
      action: `CTA Text:View Experience`, // This seems like an error, but it was how it was logged in legacy app.
      label: "Card Type:Volunteer Post",
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.CARD_SELECTED,
      properties: {
        "Card Type": "Story",
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });
  };

  const handleCardClick = () => {
    // Handle the analytics event where a user clicks the card.
    googleAnalytics.event({
      category: "Card Selection",
      action: "CTA Text:View Story",
      label: "Card Type:Story",
      value: 1,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.CARD_SELECTED,
      properties: {
        "Card Type": "Story",
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });
  };

  const hasCommentContentToShow =
    userLoading || currentUser || savedLastComment;
  /* *
   * If the user uploaded an image to their story, display the image with text underneath it.
   * Otherwise we will overlay text on top of the default background image by passing title to DecoratedImage.
   * */
  return (
    <Card
      onClick={handleCardClick}
      style={{
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 2px 2px",
      }}
      headStyle={{ padding: "0px" }}
      bodyStyle={{ padding: "0px" }}
      cover={
        images && (
          <DecoratedImage
            alt="Story Img"
            src={storyImage}
            titleClass="white fwb f-5"
            title={hasUploadedImages ? undefined : storyDescription}
            imgBrightness={hasUploadedImages ? 100 : 80}
            onClick={handleStoryClick}
            loading="lazy"
          />
        )
      }
      className={`${anim.enter_s} lift mh-auto ant-story-card bg-white pointer`}
      title={
        <CardAvatarHeader
          user={user}
          timestamp={timestamp}
          onClick={handleStoryClick}
        />
      }
    >
      {/* CONTENT ------------------------------------------------ */}
      {((hasUploadedImages && storyDescription) ||
        storyCategoryDescription) && (
        <>
          <div className="ph-5 pv-4" onClick={handleStoryClick}>
            {/* Story Description will display beneath the image only if the user had previously uploaded an image to their story. */}
            {hasUploadedImages && storyDescription && (
              <Text>{storyDescription}</Text>
            )}
            {/* Story Category - A line followed by description */}
            {storyCategoryDescription && (
              <Group xs="horizontal" spacing={2}>
                <div id="line" className="w-6">
                  <hr
                    style={{
                      backgroundColor: "#D9D9D9",
                      height: "1px",
                      border: "none",
                    }}
                  />
                </div>
                <Text type="secondary" className="f-2">
                  {storyCategoryDescription}
                </Text>
              </Group>
            )}
          </div>
          <Divider />
        </>
      )}

      {/* Only Allow Comments for organizations with showInspireComments set to true */}
      {showComments && hasCommentContentToShow && (
        <>
          <div className="ph-5 pb-4">
            {/* VIEW COMMENTS ---------------------------------------- */}
            <div id="ViewComments" className="mt-3 flex flex-column">
              {(savedLastComment || newComments.length > 0) && (
                <div className="pb-3">
                  {/* View Latest Comment (if there is one) */}
                  {savedLastComment && (
                    <CardComment
                      key={savedLastComment.id}
                      user={savedLastComment.user}
                      comment={savedLastComment.body}
                      timestamp={savedLastComment.timestamp}
                    />
                  )}
                  {/* View Added Comment (displays after user submits a comment). */}
                  {currentUser &&
                    newComments &&
                    newComments.map((comment) => {
                      return (
                        <>
                          <CardComment
                            key={comment.id}
                            user={currentUser}
                            comment={comment.body}
                            animate={true}
                            timestamp={comment.timestamp}
                          />
                        </>
                      );
                    })}
                  {commentCount > 1 && (
                    <div
                      className="add-underline"
                      style={{
                        position: "absolute",
                        bottom: 15,
                        right: 30,
                      }}
                    >
                      <Link href={storiesUrl} className="primary f-2 fwb">
                        View {commentCount - 1} additional{" "}
                        {commentCount > 1 ? "comments" : "comment"}
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* ADD A COMMENT --------------------------------------- */}
              {userLoading ? (
                <Spin style={{ textAlign: "left", margin: 24 }} />
              ) : (
                currentUser && (
                  <div onClick={handleCardCommentInputClick}>
                    <CardCommentInput
                      user={currentUser}
                      onSubmit={handleCommentInputSubmit}
                      addingComment={addingComment}
                      form={commentForm}
                    />
                  </div>
                )
              )}
            </div>
          </div>
          {((currentUser && newComments) || savedLastComment) && <Divider />}
        </>
      )}
      {/* CARD ACTIONS BAR -------------------------------------- */}
      <CardActions
        id={id}
        user={currentUser}
        cardType="story"
        likeEnabled={true}
        storiesEnabled={true}
        likes={likes}
        userHasLiked={iLiked}
      />
    </Card>
  );
};

export default StoryCard;
